import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService, MenuItem } from 'primeng/api';
import { CalendarModule } from 'primeng/calendar';
import { DropdownModule } from 'primeng/dropdown';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { FieldsetModule } from 'primeng/fieldset';
import { forkJoin, Subscription } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { InputGroupModule } from 'primeng/inputgroup';
import { FloatLabelModule } from 'primeng/floatlabel';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { FileUploadModule } from 'primeng/fileupload';
import { ProgressBarModule } from 'primeng/progressbar';
import { CommonModule } from '@angular/common';
import { Supplier } from '../../master-data/suppliers/suppliers.model';
import { Business } from '../../master-data/business/business.model';
import { BusinessService } from '../../master-data/business/business.service';
import { CustomersService } from '../../master-data/customers/customers.service';
import { Customer } from '../../master-data/customers/customers.model';
import { AppUser } from '../../users/user.model';
import { ContractService } from '../contract.service';
import { DatePicker } from 'primeng/datepicker';
import { ProgressSpinner } from 'primeng/progressspinner';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Item } from '../../master-data/items/item.model';
import { SupplierSite } from '../../master-data/suppliers/supplierSite/supplierSite.model';
import { TabsModule } from 'primeng/tabs';
import { ChatSession } from '../../models/chat-session.model';
import { ChatSessionService } from '../../services/chat-session.service';
import { MAX_FILE_SIZE_BYTES } from '../../shared/constants/file-size.constants';
import { GENERAL_UPLOAD_ACCEPT_TYPES, SPREADSHEET_UPLOAD_ACCEPT_TYPES } from '../../shared/constants/upload-file-types.constants';
import { FirebaseStorageService } from '../../services/firebase-storage.service';
import { ContractAttachment } from '../contract-attachment.model';
import { filter, map, take, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';

@Component({
  selector: 'app-add-edit-contract',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    ToastModule,
    CalendarModule,
    DropdownModule,
    InputNumberModule,
    InputTextModule,
    FieldsetModule,
    ButtonModule,
    IftaLabelModule,
    FloatLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    FileUploadModule,
    ProgressBarModule,
    TabsModule,
    DatePicker,
    ProgressSpinner,
    ConfirmDialog,
  ],
  providers: [MessageService],
  templateUrl: './contract-fixed-by-site-form.component.html',
  styleUrls: ['./contract-fixed-by-site-form.component.css'],
})
export class ContractFixedBySiteFormComponent implements OnInit, OnDestroy {
  readonly maxFileSize = MAX_FILE_SIZE_BYTES;
  readonly attachmentAcceptTypes = GENERAL_UPLOAD_ACCEPT_TYPES;
  readonly spreadsheetAcceptTypes = SPREADSHEET_UPLOAD_ACCEPT_TYPES;
  contractForm!: FormGroup;
  isLoading = false;
  isUploadingAttachments = false;
  attachmentUploadProgress = 0;
  submitted = false;
  isEditMode = false;
  contractId: string | null = null;
  currentUser: AppUser;
  // Breadcrumb items
  breadcrumbItems: MenuItem[] = [];
  homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };

    private routeSub!: Subscription;
    private sessionSub!: Subscription;
    currentSessionId: number | null = null;
    currentSession: ChatSession | undefined;

  customers: Customer[] = [];
  filteredCustomers: Customer[] = [];
  filteredSuppliers: Supplier[] = [];
  filteredSupplierSites: SupplierSite[] = [];
  filteredItems: Item[] = [];

  businesses: Business[] = [];
  filteredBusinesses: Business[] = [];

  tomorrowDate!: Date;

  suppliersPerRow: Supplier[][] = [];
  oldSuppliersPerRow: Supplier[][] = [];

  itemsPerRow: Item[][] = [];
  oldItemsPerRow: Item[][] = [];

  supplierSitesPerRow: SupplierSite[][] = [];
  oldSupplierSitesPerRow: SupplierSite[][] = [];

  CustomerfilterValue: string = '';
  BusinessfilterValue: string[] = [];
  SupplierfilterValue: string[] = [];
  itemfilterValue: string[] = [];
  SupplierSitefilterValue: string[] = [];
  private attachmentProgressByIndex = new Map<number, number>();

  activeTab = 'lines';
  uploadDetailsFile:
    | {
      fileName: string;
      fileType: string;
      fileSize: number;
      fileContent: string;
    }
    | null = null;

  constructor(
    private fb: FormBuilder,
    private messageService: MessageService,
    private router: Router,
    private businessService: BusinessService,
    private customerService: CustomersService,
    private contractService: ContractService,
    private chatSessionService: ChatSessionService,
    private route: ActivatedRoute,
    private storageService: FirebaseStorageService
  ) {
    this.currentUser = JSON.parse(
      sessionStorage.getItem('appUser') ?? ''
    ) as AppUser;
  }
 onTabChange(tabValue: string | number) {
    this.activeTab = tabValue as string;
    this.toggleDateValidators(tabValue === 'lines');
  }

  toggleDateValidators(required: boolean) {
    const startControl = this.contractForm.get('startDateActive');
    const endControl = this.contractForm.get('endDateActive');
    const customerControl = this.contractForm.get('customer');
    if (required) {
      startControl?.setValidators([Validators.required]);
      endControl?.setValidators([Validators.required]);
      customerControl?.setValidators([Validators.required]);
    } else {
      startControl?.clearValidators();
      endControl?.clearValidators();
      customerControl?.clearValidators();
      startControl?.setValue(null);
      endControl?.setValue(null);
      customerControl?.setValue(null);
    }
    startControl?.updateValueAndValidity();
    endControl?.updateValueAndValidity();
    customerControl?.updateValueAndValidity();
  }

  ngOnInit(): void {   
    let routeWithSessionId: ActivatedRoute | null = this.route;
    while (
      routeWithSessionId &&
      !routeWithSessionId.snapshot.paramMap.has('sessionId')
    ) {
      routeWithSessionId = routeWithSessionId.parent;
    }
    
    this.routeSub = (routeWithSessionId || this.route).paramMap.subscribe(
      (params) => {
        const sessionIdParam = params.get('sessionId');
        if (sessionIdParam) {
          if (sessionIdParam === 'current') {
            this.sessionSub = this.chatSessionService.sessions$.subscribe(
              (sessions) => {
                if (sessions && sessions.length > 0) {
                  const latest = sessions[sessions.length - 1];
                  this.currentSessionId = latest.sessionId;
                  this.currentSession = latest;
                }
              }
            );
          } else {
            const parsedId = Number(sessionIdParam);
            if (!isNaN(parsedId)) {
              this.currentSessionId = parsedId;
              this.sessionSub = this.chatSessionService.sessions$.subscribe(
                (sessions) => {
                  this.currentSession = sessions.find(
                    (s) => s.sessionId === this.currentSessionId
                  );
                }
              );
            } else {
              const errorMsg = 'Invalid session ID in route parameters.';
              this.messageService.add({
                severity: 'error',
                summary: 'Routing Error',
                detail: errorMsg,
              });
            }
          }
        } else {
          const errorMsg = 'Session ID not found in route parameters.';
          this.messageService.add({
            severity: 'error',
            summary: 'Routing Error',
            detail: errorMsg,
          });         
        }
      }
    );
    this.getTomorrowDate();

    this.contractId = this.route.snapshot.paramMap.get('contractId');
    this.isEditMode = !!this.contractId;

    this.initForm();
    // this.subscribeToValueChanges();

    // Load all necessary dropdown data and then proceed with form population
    this.loadDropdownData().subscribe({
      next: (data) => {
        this.customers = data.customers.content;
        this.filteredCustomers = data.customers.content;
        this.businesses = data.businesses.content;
        this.filteredBusinesses = data.businesses.content;

        // Now that dropdowns are populated, we can safely check for pre-fill or edit mode.
        if (this.isEditMode) {
          this.contractService
            .getContractById(+this.contractId!)
            .subscribe((contract) => {
              const customerToSet = this.customers.find(
                (c) => c.id === contract.customer?.id
              );
              const businessToSet = this.businesses.find(
                (b) => b.id === contract.business?.id
              );
            });
        } else {
        }
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load required form data.',
        });
        console.error('Error loading dropdown data:', err);
      },
    });

    // Initialize validators based on default tab
    this.toggleDateValidators(this.activeTab === 'lines');
  }

  private initForm(): void {
    this.contractForm = this.fb.group({
      // Contract Request
      contractName: ['', Validators.required],
      startDateActive: ['', Validators.required],
      endDateActive: ['', Validators.required],
      customer: [[], Validators.required],
      // business: [null],
      contractNote1: [''],
      contractDetails: this.fb.array([], Validators.required), // form array
      attachments: this.fb.array([]),
    });
    // أول عنصر افتراضي
    this.addItemToContractDetail();
  }

  isInvalid(controlName: string): boolean {
    const control = this.contractForm.get(controlName);
    return !!(
      control &&
      control.invalid &&
      (control.dirty || control.touched || this.submitted)
    );
  }
  isDetailsInvalid(index: number, controlName: string): boolean {
    const detailArray = this.contractForm.get('contractDetails') as FormArray;
    const control = detailArray.at(index).get(controlName);

    return !!(
      control &&
      control.invalid &&
      (control.dirty || control.touched || this.submitted)
    );
  }

  get contractDetails(): FormArray {
    return this.contractForm.get('contractDetails') as FormArray;
  }

  get hasUploadFile(): boolean {
    return !!this.uploadDetailsFile;
  }

  get hasLineItemsData(): boolean {
    if (!this.contractDetails || this.contractDetails.length === 0) {
      return false;
    }
    return this.contractDetails.controls.some((control) => {
      const group = control as FormGroup;
      const value = group.value;
      return Object.keys(value).some((key) => {
        const fieldValue = value[key];
        return (
          fieldValue !== null &&
          fieldValue !== undefined &&
          fieldValue !== ''
        );
      });
    });
  }

  addItemToContractDetail() {
    const detail = this.fb.group({
      business: [null, Validators.required],
      vendor: [null, Validators.required],
      vendorSite: [null, Validators.required],
      item: [null],
      rebate: [
        null,
        [Validators.required, Validators.min(0), Validators.max(100)],
      ],
      jbp: [null],
      fixedJbp: [null],
      slab1: [null],
      slab1Percent: [null, [Validators.min(0), Validators.max(100)]],
      slab2: [null],
      slab2Percent: [null, [Validators.min(0), Validators.max(100)]],
      slab3: [null],
      slab3Percent: [null, [Validators.min(0), Validators.max(100)]],
      deliverables: [''],
    });
    this.contractDetails.push(detail);
  }

  removeItemFromContractDetail(index: number) {
    this.contractDetails.removeAt(index);
  }

  validatePercentage(event: any, fieldName: string, index: number) {
    console.log(index, fieldName, event.target.value);
    const value = Number(event.target.value);
    const detailArray = this.contractForm.get('contractDetails') as FormArray;
    detailArray.at(index).get(fieldName)?.reset();

    if (value < 0) {
      detailArray.at(index).get(fieldName)?.setValue(1);
    } else if (value > 100) {
      detailArray.at(index).get(fieldName)?.setValue(100);
    } else {
      detailArray.at(index).get(fieldName)?.setValue(value);
    }
  }
  submitForm() {
    this.submitted = true;

    if (this.hasUploadFile && this.hasLineItemsData) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Please use either line items or file upload, not both.',
      });
      return;
    }

    if (this.hasUploadFile) {
      this.setContractDetailsValidation(false);
    } else {
      this.setContractDetailsValidation(true);
    }

    if (this.contractForm.valid) {
      const contractFormClone = this.contractForm.value;
      this.isLoading = true;

      const formData = new FormData();
      let contractJson: any;
      const pendingAttachments = this.attachments.controls
        .filter(control => !control.get('attachmentId')?.value && control.get('file')?.value)
        .map(control => ({
          file: control.get('file')?.value as File,
          fileName: control.get('fileName')?.value as string,
          fileType: control.get('fileType')?.value as string,
          fileSize: control.get('fileSize')?.value as number,
        }));

      if (this.hasUploadFile && this.uploadDetailsFile) {
        // Prepare contract JSON for Upload Mode and keep attachment metadata in the payload.
        contractJson = {
          contractName: contractFormClone.contractName,
          customer: contractFormClone.customer,
          contractNote1: contractFormClone.contractNote1 ?? '',
          hasUploadedFile: true,
          contractType: 'FIXED',
        };

        // Add the details file as Blob
        const fileBlob = this.base64ToBlob(
          this.uploadDetailsFile.fileContent,
          this.uploadDetailsFile.fileType
        );
        formData.append(
          'contractFile',
          fileBlob,
          this.uploadDetailsFile.fileName
        );
      } else {
        // Prepare contract JSON for Lines Mode
        contractJson = {
          ...contractFormClone,
          startDateActive: this.formatDateToLocal(
            contractFormClone.startDateActive
          ),
          endDateActive: this.formatDateToLocal(contractFormClone.endDateActive),
          hasUploadedFile: false,
          contractType: 'FIXED',
        };
      }

      delete (contractJson as any).attachments;

      // Add contract data as JSON blob
      formData.append(
        'contract',
        new Blob([JSON.stringify(contractJson)], { type: 'application/json' })
      );

      // Add attachments as Blobs
      const attachmentsArray = contractFormClone.attachments || [];
      attachmentsArray.forEach((attachment: any) => {
        const attachmentBlob = this.base64ToBlob(
          attachment.fileContent,
          attachment.fileType
        );
        formData.append('attachments', attachmentBlob, attachment.fileName);
      });

      console.log('Session ID:', this.currentSessionId);
      if (
        typeof this.currentSessionId === 'number' &&
        Number.isFinite(this.currentSessionId) &&
        this.currentSessionId > 0
      ) {
        formData.append('sessionId', this.currentSessionId.toString());
      }
    if (this.currentSessionId) {
      formData.append('sessionId', this.currentSessionId.toString());
    }

      this.contractService.createSalesContract(formData).subscribe({
        next: (data) => {
          this.uploadContractAttachments(data.contractId!, pendingAttachments).subscribe({
            next: () => {
              this.isLoading = false;
              this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Contract saved successfully.',
              });
              setTimeout(
                () =>
                  this.router.navigate([
                    '/ai-studio/session/current',
                    { outlets: { canvasOutlet: ['rebate', 'list'] } },
                  ]),
                1500
              );
            },
            error: (err) => {
              this.isLoading = false;
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Contract saved, but attachment upload failed.',
              });
              console.error('Attachment upload failed after contract save:', err);
            }
          });
        },
        error: (err) => {
          this.isLoading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to save contract.',
          });
          console.error('Error saving contract:', err);
        },
      });
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Please fill in all required fields.',
      });
    }
  }

  private setContractDetailsValidation(requireLines: boolean): void {
    const detailsArray = this.contractDetails;
    if (!detailsArray) return;

    detailsArray.setValidators(requireLines ? Validators.required : null);
    for (let i = 0; i < detailsArray.length; i++) {
      const group = detailsArray.at(i) as FormGroup;

      const businessCtrl = group.get('business');
      const vendorCtrl = group.get('vendor');
      const vendorSiteCtrl = group.get('vendorSite');
      const rebateCtrl = group.get('rebate');
      const slab1PercentCtrl = group.get('slab1Percent');
      const slab2PercentCtrl = group.get('slab2Percent');
      const slab3PercentCtrl = group.get('slab3Percent');

      if (requireLines) {
        businessCtrl?.setValidators(Validators.required);
        vendorCtrl?.setValidators(Validators.required);
        vendorSiteCtrl?.setValidators(Validators.required);
        rebateCtrl?.setValidators([Validators.required, Validators.min(0), Validators.max(100)]);
        slab1PercentCtrl?.setValidators([Validators.min(0), Validators.max(100)]);
        slab2PercentCtrl?.setValidators([Validators.min(0), Validators.max(100)]);
        slab3PercentCtrl?.setValidators([Validators.min(0), Validators.max(100)]);
      } else {
        businessCtrl?.setValidators(null);
        vendorCtrl?.setValidators(null);
        vendorSiteCtrl?.setValidators(null);
        rebateCtrl?.setValidators(null);
        slab1PercentCtrl?.setValidators(null);
        slab2PercentCtrl?.setValidators(null);
        slab3PercentCtrl?.setValidators(null);
      }

      businessCtrl?.updateValueAndValidity({ emitEvent: false });
      vendorCtrl?.updateValueAndValidity({ emitEvent: false });
      vendorSiteCtrl?.updateValueAndValidity({ emitEvent: false });
      rebateCtrl?.updateValueAndValidity({ emitEvent: false });
      slab1PercentCtrl?.updateValueAndValidity({ emitEvent: false });
      slab2PercentCtrl?.updateValueAndValidity({ emitEvent: false });
      slab3PercentCtrl?.updateValueAndValidity({ emitEvent: false });
    }
    detailsArray.updateValueAndValidity({ emitEvent: false });
  }

  getTomorrowDate() {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    this.tomorrowDate = tomorrow;
  }

  onCancel(): void {
    this.router.navigate([
      '/ai-studio/session/current',
      { outlets: { canvasOutlet: ['rebate', 'list'] } },
    ]);
  }

  private loadDropdownData() {
    return forkJoin({
      customers: this.customerService.getAllCustomers({
        page: 0,
        size: 10000, //2000
        loadLocations: false,
      }),
      businesses: this.businessService.getAllBusinesss({
        page: 0,
        size: 10000,
        loadSuppliers: true,
        loadItems: true,
        loadSites: true,
      }),
    });
  }

  filterCustomers(event: any) {
    this.CustomerfilterValue = event.target.value;
    if (!event.target.value || event.target.value.length === 0) {
      this.filteredCustomers = this.customers;
    } else {
      this.filteredCustomers = this.customers.filter(
        (customer) =>
          customer.customerName
            .toLowerCase()
            .includes(event.target.value.toLowerCase()) ||
          customer.customerNumber
            .toLowerCase()
            .includes(event.target.value.toLowerCase())
      );
    }
    if (this.filteredCustomers.length === 1) {
      this.contractForm.get('customer')?.setValue(this.filteredCustomers[0]);
    }
  }

  filterBusinesses(event: any, index: number) {
    this.BusinessfilterValue[index] = event.target.value ?? '';
    if (!event.target.value || event.target.value.length === 0) {
      this.filteredBusinesses = this.businesses;
    } else {
      this.filteredBusinesses = this.businesses.filter(
        (business) =>
          business.businessName
            .toLowerCase()
            .includes(event.target.value.toLowerCase()) ||
          (business.businessCode &&
            business.businessCode
              .toLowerCase()
              .includes(event.target.value.toLowerCase()))
      );
    }
  }

  filterSuppliers(event: any, index: number) {
    this.SupplierfilterValue[index] = event.target.value ?? '';
    if (!event.target.value || event.target.value.length === 0) {
      this.suppliersPerRow[index] = this.oldSuppliersPerRow[index];
    } else {
      this.suppliersPerRow[index] = this.suppliersPerRow[index].filter(
        (supplier) =>
          supplier.supplierName
            .toLowerCase()
            .includes(event.target.value.toLowerCase()) ||
          (supplier.supplierNumber &&
            supplier.supplierNumber
              .toLowerCase()
              .includes(event.target.value.toLowerCase()))
      );
    }
  }

  filterItems(event: any, index: number) {
    this.itemfilterValue[index] = event.target.value ?? '';
    if (!event.target.value || event.target.value.length === 0) {
      this.itemsPerRow[index] = this.oldItemsPerRow[index];
    } else {
      this.itemsPerRow[index] = this.itemsPerRow[index].filter(
        (item) =>
          item.erpItemNameE
            ?.toLowerCase()
            .includes(event.target.value.toLowerCase()) ||
          (item.erpItemNumber &&
            item.erpItemNumber
              .toLowerCase()
              .includes(event.target.value.toLowerCase()))
      );
    }
  }

  filterSupplierSites(event: any, index: number) {
    this.SupplierSitefilterValue[index] = event.target.value ?? '';
    if (!event.target.value || event.target.value.length === 0) {
      this.supplierSitesPerRow[index] = this.oldSupplierSitesPerRow[index];
    } else {
      this.supplierSitesPerRow[index] = this.supplierSitesPerRow[index].filter(
        (supplierSite) =>
          supplierSite.supplierSiteName
            .toLowerCase()
            .includes(event.target.value.toLowerCase()) ||
          (supplierSite.supplierSiteNumber &&
            supplierSite.supplierSiteNumber
              .toLowerCase()
              .includes(event.target.value.toLowerCase()))
      );
    }
  }

  ngOnDestroy(): void { }

  get attachments(): FormArray {
    return this.contractForm.get('attachments') as FormArray;
  }

  onFileSelect(event: any): void {
    const files: File[] = Array.isArray(event.files)
      ? event.files
      : Array.from(event.files ?? []);
    if (files.length === 0) {
      return;
    }

    files.forEach(file => {
      this.attachments.push(
        this.fb.group({
          fileName: [file.name],
          fileType: [file.type],
          fileSize: [file.size],
          file: [file],
        })
      );
    });
  }

  onUploadFileSelect(event: any): void {
    const file = event.files && event.files.length > 0 ? event.files[0] : null;
    if (!file) {
      return;
    }
    const reader = new FileReader();
    reader.onload = (e: any) => {
      const base64String = e.target.result;
      this.uploadDetailsFile = {
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        fileContent: base64String,
      };
    };
    reader.readAsDataURL(file);
  }

  removeAttachment(index: number): void {
    this.attachments.removeAt(index);
  }

  private uploadContractAttachments(contractId: number, pendingAttachments: Array<{
    file: File;
    fileName: string;
    fileType: string;
    fileSize: number;
  }>) {
    if (pendingAttachments.length === 0) {
      return of([]);
    }

    this.isUploadingAttachments = true;
    this.attachmentUploadProgress = 0;
    this.attachmentProgressByIndex.clear();
    const contractFolder = `contracts/${contractId}`;

    return forkJoin(
      pendingAttachments.map((item, index) => {
        const safeFileName = item.fileName.replace(/[^a-zA-Z0-9.\-_]/g, '_');
        const storagePath = `${contractFolder}/${Date.now()}-${safeFileName}`;
        return this.storageService.uploadFileWithProgress(item.file, storagePath).pipe(
          filter(event => {
            this.updateAttachmentProgress(index, event.progress, pendingAttachments.length);
            return !!event.downloadURL;
          }),
          take(1),
          map(event => ({
            fileName: item.fileName,
            fileType: item.fileType,
            fileSize: item.fileSize,
            fileUrl: event.downloadURL!,
            uploadedBy: this.currentUser?.fullName || this.currentUser?.email || 'N/A',
            uploadedDate: new Date().toISOString(),
          } as ContractAttachment))
        );
      })
    ).pipe(
      switchMap(uploadedAttachments => this.contractService.uploadAttachments(contractId, uploadedAttachments)),
      map(savedAttachments => {
        this.isUploadingAttachments = false;
        return savedAttachments;
      })
    );
  }

  private updateAttachmentProgress(index: number, progress: number, totalFiles: number): void {
    this.attachmentProgressByIndex.set(index, progress);
    const totalProgress = Array.from(this.attachmentProgressByIndex.values()).reduce((sum, value) => sum + value, 0);
    this.attachmentUploadProgress = Math.round(totalProgress / Math.max(totalFiles, 1));
  }

  formatDateToLocal(date: Date | null): string | null {
    if (!date) return null;

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // JS months = 0-11
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  onBusinessChange(index: number, business: Business) {
    if (this.suppliersPerRow.length <= index) {
      this.suppliersPerRow.push([]);
    }
    this.suppliersPerRow[index] = business.suppliers ?? [];
    this.oldSuppliersPerRow[index] = business.suppliers ?? [];
    const detailArray = this.contractForm.get('contractDetails') as FormArray;
    detailArray.at(index).get('vendor')?.reset();

    if (this.suppliersPerRow[index].length === 1) {
      detailArray
        .at(index)
        .get('vendor')
        ?.setValue(this.suppliersPerRow[index][0]);
    }

    if (this.itemsPerRow.length <= index) {
      this.itemsPerRow.push([]);
    }
    this.itemsPerRow[index] = business.items ?? [];
    this.oldItemsPerRow[index] = business.items ?? [];
    detailArray.at(index).get('item')?.reset();

    if (this.itemsPerRow[index].length === 1) {
      detailArray.at(index).get('item')?.setValue(this.itemsPerRow[index][0]);
    }

    if (this.supplierSitesPerRow.length <= index) {
      this.supplierSitesPerRow.push([]);
    }
    this.supplierSitesPerRow[index] = business.supplierSites ?? [];
    this.oldSupplierSitesPerRow[index] = business.supplierSites ?? [];
    detailArray.at(index).get('vendorSite')?.reset();

    if (this.supplierSitesPerRow[index].length === 1) {
      detailArray
        .at(index)
        .get('vendorSite')
        ?.setValue(this.supplierSitesPerRow[index][0]);
    }
  }

  private base64ToBlob(base64Data: string, contentType: string): Blob {
    // Remove data URL prefix if present (e.g., "data:image/png;base64,")
    const base64 = base64Data.includes('base64,')
      ? base64Data.split('base64,')[1]
      : base64Data;

    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: contentType });
  }
}
